import React, { useEffect, useState } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { renderRoutes } from "react-router-config";
import { connect } from "react-redux";
import { parse } from "query-string";
import I18n from "../../I18n";
import theme from "../../style/_theme.json";
import proxy from "../../routes/proxy";
import { mapProperties, stringifyFilter } from "colbi_web_ui/lib/utils";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    toggleMenu,
    setLocale,
    removeModal,
    saveWorkerAssociationPackageAction,
} from "../../store/actions";
import { ToastContainer } from "react-toastify";

import { LoadingIndicator, ImageRender } from "colbi_web_ui/lib/components";
import Header from "colbi_web_ui/lib/components/layout/Header/Header";

import { GlobalProvider as ColbiUIProvider } from "colbi_web_ui/lib/state/GlobalProvider";
import { GlobalProvider } from "../../state/globalProvider";
import ModalController from "../App/ModalController/ModalController";

import { manageNavigation } from "./navigation";

import styles from "./Settings.module.sass";

import { UserRoles } from "../../enums/userRoles";
import DialogController from "../App/DialogController/DialogController";
import configsByTheme from "../../enums/configsByTheme";
import { REACT_APP_COLBI_THEME } from "../../environmentVariables";
import MainTabs from "../App/MainTabs/MainTabs";
import SovosProvider from "mosaic-react/sovos-provider";
import SovosNavigation from "mosaic-react/sovos-navigation";
import {
    footerProps,
    setAccount,
    decodeUserSession,
    buildStaticLinks,
} from "../../utils/utils";
import LanguageDialog from "../../components/LanguageDialog/LanguageDialog";
import { getOrganizationName } from "../../utils/camelize";
import {
    ArrowBack,
    AssignmentTurnedIn,
    AutoGraph,
    Dashboard,
    SettingsSuggest,
} from "mosaic-react-icons";
import { checkAccessByRole } from "../../utils/roleValidatorUtil";
import { deepLink } from "../../utils/routingUtils";

const allTabs = [
    {
        section: "organizations",
        label: "Organizations",
        createType: "Organization",
    },
    {
        section: "projects",
        label: "Projects",
        createType: "Project",
    },
    {
        section: "users",
        label: "Users",
        createType: "User",
    },
];

const filtersTabs = [
    {
        section: "projects",
        label: "Projects",
        createType: "Project",
    },
    {
        section: "users",
        label: "Users",
        createType: "User",
    },
];

const Provider = ({ children, ...props }) => {
    return (
        <GlobalProvider {...props}>
            <ColbiUIProvider {...props}>{children}</ColbiUIProvider>
        </GlobalProvider>
    );
};

const appTheme = REACT_APP_COLBI_THEME ? REACT_APP_COLBI_THEME.toLocaleLowerCase() : "";
const asideMenuLogo = configsByTheme(appTheme)["icon"];
const localesByAppTheme = configsByTheme(appTheme)["locales"];
const freshDeskFormTitleByAppTheme = configsByTheme(appTheme)["freshDeskFormTitle"];

const logo = () => {
    return (
        appTheme && <ImageRender icon={`${appTheme && appTheme.length ? asideMenuLogo : "logo"}`} />
    );
};

const PackagesAssociation = (props) => {
    const {
        user,
        fetch,
        match,
        setLocale,
        location,
        toggleMenu,
        logout,
        history,
        pushModal,
        popModal,
        menu,
        reload,
        pushDialog,
        uploads,
        route,
        modules,
        saveWorkerAssociationPackage,
    } = props;

    const locale = match.params.locale || "en";
    const i18n = I18n.use(locale);
    const pathName = location.pathname;

    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
    const [userLocale, setUserLocale] = useState(locale);
    const [selectedLink, setSelectedLink] = useState(i18n("overview"));

    useEffect(() => {
        if (location && location.state && location.state.selectedLink) {
            setSelectedLink(i18n(location.state.selectedLink));
        }
    }, [location, i18n]);

    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);

    const userInformation = decodeUserSession(user);

    const isAdminRoles = !checkAccessByRole(userInformation, [
        UserRoles.OWNER,
        UserRoles.READER,
        UserRoles.WRITER,
    ]);

    const tabs = !checkAccessByRole(userInformation, [UserRoles.ADMIN, UserRoles.BACKOFFICE])
        ? filtersTabs
        : allTabs;

    const manageNavigationItems = [
        {
            name: "Home",
            to: "/",
            exact: true,
            icon: ArrowBack,
        },
    ];

    if (isAdminRoles) {
        manageNavigationItems.unshift(
            {
                name: "Rules",
                to: "/settings/nc_rules",
                exact: true,
                icon: AssignmentTurnedIn,
            },
            {
                name: "Kpis",
                to: "/settings/kpis",
                exact: true,
                icon: AutoGraph,
            },
            {
                name: "Custom Reports",
                to: "/settings/custom_reports",
                exact: true,
                icon: SettingsSuggest,
            },
            {
                name: "Packages",
                to: "/settings/packages",
                exact: true,
                icon: Dashboard,
            },
        );

        tabs.push(
            { section: "packages", label: "Packages", createType: "Package" },
            {
                section: "custom_reports",
                label: "Custom Reports",
                createType: "CustomReportSettings",
            },
            { section: "kpis", label: "Kpis", createType: "Kpi" },
            { section: "nc_rules", label: "Rules", createType: "NcRule" },
        );
    }

    const navigation = manageNavigation(manageNavigationItems);

    const setupLinks = buildStaticLinks(
        navigation,
        history,
        match.params,
        i18n,
    );

    useEffect(() => {
        deepLink(setupLinks, pathName, setSelectedLink);
    }, [pathName, setupLinks]);

    if (!userInformation) {
        return <Redirect to={"/login"} push />;
    }

    if (userInformation && userInformation.expired) {
        return <Redirect to={`/${locale}/reset_password`} />;
    }

    if (!(userInformation && userInformation.accessSettings) || !isAdminRoles) {
        return <Redirect to={`/${locale}/unauthorized`} />;
    }

    if (
        !checkAccessByRole(userInformation, [
            UserRoles.ADMIN,
            UserRoles.BACKOFFICE,
            UserRoles.PP_SUPPORT,
        ])
    ) {
        navigation.shift();
    }

    if (checkAccessByRole(userInformation, [UserRoles.BACKOFFICE])) {
        navigation.pop();
    }

    navigation.forEach((elem) => {
        elem.name = I18n.translate`${elem.name}`;
        return elem;
    });

    const queryParams = parse(location.search) || {};
    const routeParams = match.params || {};
    const params = {
        ...queryParams,
        ...routeParams,
    };

    const ticketSubjectEnvironment = fetch.results[`${locale}_get_environment_to_subject`] || "";

    const isToDisplaySupportIcon =
        (fetch.results[`${locale}_is_to_display_support_icon`] || "false").toLocaleLowerCase() ===
        "true";

    const goto = (page, filters, anchor) => {
        const pageUrl = page
            ? (typeof page.join === "function" ? page.join("/") : page).replace(/\/+/g, "/")
            : null;
        const urlFilters = filters
            ? Array.isArray(filters)
                ? stringifyFilter(filters)
                : stringifyFilter([filters])
            : "";
        const localePrefixed = pageUrl
            ? `${locale ? `/${locale}${!pageUrl.match(/^\//) ? "/" : ""}` : ""}`
            : null;
        const gotoPage = pageUrl
            ? `${localePrefixed}${pageUrl}${
                  urlFilters.length ? `?filter=${urlFilters}&resetFilters=${Date.now()}` : ""
              }`
            : `${location.pathname}?filter=${urlFilters}&resetFilters=${Date.now()}${
                  anchor ? `#${anchor}` : ""
              }`;
        history.push(gotoPage);
    };

    const routeQueries = (route.queries && route.queries(params)) || [];

    let displayComponents = [];

    displayComponents.push(...route.components);

    const children = displayComponents.map((c, index) => {
        const Component = c.component;
        const componentParsedProps = mapProperties(
            c.props,
            (fetch || {}).results,
            (fetch || {}).status,
            routeQueries,
            props,
            params,
            i18n,
            locale,
        );
        const componentProps = {
            ...componentParsedProps,
            modules,
        };

        return (
            <Component
                key={index}
                modifiers={c.modifiers}
                {...componentProps}
                activity={fetch.activity}
            />
        );
    });

    const metadata = getOrganizationName();

    return (
        <SovosProvider>
            <div className={styles["app"]}>
                <Provider
                    user={userInformation}
                    proxy={proxy}
                    theme={theme}
                    uploads={uploads}
                    actions={{
                        pushModal,
                        pushDialog,
                        popModal,
                        saveWorkerAssociationPackage,
                        removeModal,
                        logout,
                        setLocale: (locale) => {
                            history.push(`/${locale}${history.location.pathname.substring(3)}`);
                        },
                        goto,
                    }}
                    i18n={i18n}
                    locale={locale || localesByAppTheme[0].id}
                    locales={localesByAppTheme}
                    reload={reload}
                    params={params}
                    history={history}
                    location={location}
                >
                    <div className={`${styles["aside"]} `}>
                        <SovosNavigation
                            product="APR SAF-T"
                            links={setupLinks}
                            selectedLink={selectedLink}
                            setAccount={(account) => {
                                setAccount(account, locale, history, proxy, params);
                            }}
                            FooterProps={footerProps(
                                history,
                                locale,
                                userInformation,
                                setIsLanguageModalOpen,
                                logout,
                                i18n,
                            )}
                        />

                        <LanguageDialog
                            isOpen={isLanguageModalOpen}
                            setIsOpen={setIsLanguageModalOpen}
                            i18n={i18n}
                            setUserLocale={setUserLocale}
                            userLocale={userLocale}
                            localesByAppTheme={localesByAppTheme}
                            setLocale={setLocale}
                            history={history}
                        />
                    </div>
                    <div className={styles["main"]}>
                        <Header
                            className={styles["header"]}
                            logout={logout}
                            navigation={navigation}
                            locales={localesByAppTheme}
                            i18n={i18n}
                            locale={locale}
                            search={false}
                            history={history}
                            params={{
                                ...match.params,
                            }}
                            theme={theme}
                            title={i18n`Packages Association`}
                            toggleMenu={toggleMenu}
                            expanded={menu !== "expanded"}
                            menuLogo={logo}
                            menuTitle={i18n`Settings`}
                            displaySupportIcon={isToDisplaySupportIcon}
                            environment={ticketSubjectEnvironment}
                            emailFrom={userInformation.email}
                            name={userInformation.name}
                            metadata={metadata}
                            freshDeskModalTitle={freshDeskFormTitleByAppTheme}
                        />
                        <main
                            className={`${styles["content"] || ""} ${styles["foreground"] || ""}`}
                        >
                            {fetch.activity && (
                                <LoadingIndicator
                                    className={`${styles["loading-indicator"]} ${
                                        menu === "expanded" ? "" : styles["small-margin"]
                                    }`}
                                />
                            )}
                            {children}
                            {route.routes && route.routes.length > 1 && (
                                <MainTabs
                                    className={styles["tabs"]}
                                    routes={route.routes}
                                    params={match.params}
                                />
                            )}
                            {renderRoutes(route.routes)}
                        </main>
                        <ModalController />
                        <ToastContainer />
                    </div>
                    <DialogController i18n={i18n} />
                </Provider>
            </div>
        </SovosProvider>
    );
};

PackagesAssociation.queries = () => {
    return [
        {
            resource: "application_version",
            body: "version",
        },
        {
            resource: "is_to_display_support_icon",
        },
        {
            resource: "get_environment_to_subject",
        },
    ];
};

export default connect(
    ({ user, fetch, menu, uploads }) => ({
        user,
        fetch,
        menu,
        uploads,
    }),
    {
        toggleMenu,
        logout,
        setLocale,
        pushModal,
        popModal,
        removeModal,
        pushDialog,
        saveWorkerAssociationPackage: saveWorkerAssociationPackageAction,
    },
)(withRouter(PackagesAssociation));
